import { useLocation } from "react-router";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../../../components/ui/table";
import { useEffect, useState } from "react";
import { Modal } from "../../../components/ui/modal";
import Form from "../../../components/form/Form";
import Label from "../../../components/form/Label";
import Input from "../../../components/form/input/InputField";
import Select from "../../../components/form/Select";
import Button from "../../../components/ui/button/Button";

const apiUrl = process.env.REACT_APP_API_URL;

interface RemedialActionsProps {
  inspectionHoistory?: any;
}

export default function RemedialActions({ inspectionHoistory }: RemedialActionsProps) {
  const location = useLocation();
  const inspectionData = inspectionHoistory || location.state?.inspectionHoistory;
  const compInspectionId = inspectionData?.compinspection_id;
  const token = localStorage.getItem("token");
  const [remedialActions, setRemedialActions] = useState<any[]>([]);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedRemedialAction, setSelectedRemedialAction] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    inspectionaction_id: "",
    inspectionType: "",
    inspectionDate: "",
    venue: "",
    createdBy: "",
    actionDate: "",
    actionTitle: "",
    dateCompleted: "",
    status: "",
    inspectionItem: "",
    remarks: "",
  });

  // Fetch remedial actions based on compinspection_id
  const fetchRemedialActionsByInspectionHistory = async () => {
    if (!compInspectionId) {
      console.error("compinspection_id is missing");
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/inspection_history/action/${compInspectionId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      if (result.success) {
        setRemedialActions(result.remedialActions);
      }else {
        setRemedialActions([]);
      }
    } catch (error) {
      console.error("Error fetching remedial actions:", error);
    }
  };

  useEffect(() => {
    fetchRemedialActionsByInspectionHistory();
  }, [compInspectionId]);

  useEffect(() => {
    if (!selectedRemedialAction || !isUpdateModalOpen) return;

    setFormData({
      inspectionaction_id: selectedRemedialAction.inspectionaction_id || "",
      inspectionType: selectedRemedialAction.insptype || "",
      inspectionDate: selectedRemedialAction.inspdate?.split("T")[0] || "",
      venue: selectedRemedialAction.venue || "",
      createdBy: selectedRemedialAction.createdby || "",
      actionDate: selectedRemedialAction.actiondate?.split("T")[0] || "",
      actionTitle: selectedRemedialAction.name || "",
      dateCompleted:
        selectedRemedialAction.datecompleted &&
        selectedRemedialAction.datecompleted !== "1900-01-01T00:00:00.000Z"
          ? selectedRemedialAction.datecompleted.split("T")[0]
          : "",
      status: selectedRemedialAction.status || "",
      inspectionItem: selectedRemedialAction.task || "",
      remarks: selectedRemedialAction.remarks || "",
    });
  }, [selectedRemedialAction, isUpdateModalOpen]);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleRowClick = (remedialAction: any) => {
    setSelectedRemedialAction(remedialAction);
    setIsUpdateModalOpen(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmitUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      inspectionaction_id: formData.inspectionaction_id,
      inspectionType: formData.inspectionType,
      inspectionDate: formData.inspectionDate,
      venue: formData.venue,
      createdBy: formData.createdBy,
      actionDate: formData.actionDate,
      actionTitle: formData.actionTitle,
      dateCompleted: formData.dateCompleted,
      status: formData.status,
      inspectionItem: formData.inspectionItem,
      remarks: formData.remarks,
    };

    try {
      const response = await fetch(`${apiUrl}/remedial_actions`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      setIsUpdateModalOpen(false);
      await fetchRemedialActionsByInspectionHistory();
    } catch (error) {
      console.error("Error updating remedial action:", error);
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString || dateString.startsWith("1900-01-01")) return "";
    const localDateString = dateString.replace(/Z$/, '');
    // local time formatte
    const date = new Date(localDateString); 

    const datePart = date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    return `${datePart}`;
  };

  return(
    <>
      <Table>
        <TableHeader  className="bg-[#d9ffa3]">
          <TableRow>
            <TableCell isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex gap-3">
                  <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                    Inspection Item
                  </span>
                </div>
              </div>
            </TableCell>
            <TableCell isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex gap-3">
                  <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                    Action Date
                  </span>
                </div>
              </div>
            </TableCell>
            <TableCell isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex gap-3">
                  <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                    Action Title
                  </span>
                </div>
              </div>
            </TableCell>
            <TableCell isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex gap-3">
                  <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                    Status
                  </span>
                </div>
              </div>
            </TableCell>
            <TableCell isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex gap-3">
                  <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                    Date Completed
                  </span>
                </div>
              </div>
            </TableCell>
            <TableCell isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex gap-3">
                  <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                    Completed By
                  </span>
                </div>
              </div>
            </TableCell>
            <TableCell isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex gap-3">
                  <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                    Created By
                  </span>
                </div>
              </div>
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {remedialActions.map((remedialAction) => (  
          <TableRow
            className="cursor-pointer hover:bg-[#ffffa3]"
            key={remedialAction.inspectionaction_id}
            onClick={() => handleRowClick(remedialAction)}
          >
            <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
              <span>{remedialAction.task}</span>
            </TableCell>
            <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
              <span>{formatDateTime(remedialAction.actiondate)}</span>
            </TableCell>
            <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
              <span>{remedialAction.name}</span>
            </TableCell>
            <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
              <span>{remedialAction.status}</span>
            </TableCell>
            <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
              <span>{formatDateTime(remedialAction.datecompleted)}</span>
            </TableCell>
            <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
              <span>{remedialAction.completedby}</span>
            </TableCell>
            <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
              <span>{remedialAction.createdby}</span>
            </TableCell>
          </TableRow>
        ))}
        </TableBody>
      </Table>

      <Modal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        className="max-w-4xl p-6 m-4"
      >
        <h4 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
         {formData.inspectionItem || "Remedial Action Required Record"}
        </h4>

        <Form onSubmit={handleSubmitUpdate}>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="space-y-6">
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <Label>Action Date</Label>
                    <Input type="date" name="actionDate" value={formData.actionDate} onChange={handleChange} />
                  </div>
                  <div>
                    <Label>Action Title</Label>
                    <Input name="actionTitle" value={formData.actionTitle} onChange={handleChange} />
                  </div>
                  <div>
                    <Label>Date Completed</Label>
                    <Input type="date" name="dateCompleted" value={formData.dateCompleted} onChange={handleChange} />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select
                      options={[
                        { value: "Completed", label: "Completed" },
                        { value: "Not Completed", label: "Not Completed" },
                      ]}
                      value={formData.status}
                      placeholder="Select an option"
                      onChange={(val) => {
                        setFormData((prev) => ({
                          ...prev,
                          status: val,
                          dateCompleted: val === "Completed" ? getTodayDate() : "",
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div>
                  <Label>Remedial Action Remarks</Label>
                  <textarea
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleChange}
                    className="w-full rounded-lg border px-4 py-2.5 text-sm shadow-theme-xs focus:outline-none text-gray-600 border-gray-300 focus:border-brand-300 focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
                    rows={2}
                  />
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <Label>Remedial Action Remarks History</Label>
                  <textarea
                    className="w-full rounded-lg border px-4 py-2.5 text-sm shadow-theme-xs focus:outline-none text-gray-600 border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                    rows={6}
                    value={selectedRemedialAction?.remarks || ""}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-2 flex justify-end gap-3">
            <Button type="button" size="sm" variant="outline" onClick={() => setIsUpdateModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm">
              Update
            </Button>
          </div>
        </Form>
      </Modal>
    
    </>
  )
}
